import { api } from "./api";
import { clerkClient } from "@/App";
import { useQuery } from "@tanstack/react-query";
import type { FlightRestrictionZoneResponse } from "./flight-restriction-zone-types";

// Re-export types for convenience
export type { FlightRestrictionZoneResponse } from "./flight-restriction-zone-types";

/**
 * Fetch all flight restriction zones accessible to the current user
 * Returns an array of FlightRestrictionZoneResponse
 */
export const fetchFlightRestrictionZones = async (): Promise<FlightRestrictionZoneResponse[]> => {
  const authToken = await clerkClient.session?.getToken();
  if (!authToken) {
    throw new Error("Authentication required");
  }
  const res = await api.get<FlightRestrictionZoneResponse[]>("/flight-restriction-zones", {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return res.data;
};

/**
 * Fetch a single flight restriction zone by designator
 * @param designator - The zone designator
 */
export const fetchFlightRestrictionZoneByDesignator = async (designator: string): Promise<FlightRestrictionZoneResponse> => {
  const authToken = await clerkClient.session?.getToken();
  if (!authToken) {
    throw new Error("Authentication required");
  }
  const res = await api.get<FlightRestrictionZoneResponse>(`/flight-restriction-zones/${designator}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return res.data;
};

// React Query hooks

export const useFlightRestrictionZonesQueryV1 = () => {
  return useQuery({
    queryKey: ["flight-restriction-zones", "v1"],
    queryFn: fetchFlightRestrictionZones,
    staleTime: 30 * 60 * 1000, // 30 minutes - flight restriction zones don't change often
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus for static data
  });
};

export const useFlightRestrictionZoneQueryV1 = (designator: string) => {
  return useQuery({
    queryKey: ["flight-restriction-zone", "v1", designator],
    queryFn: () => fetchFlightRestrictionZoneByDesignator(designator),
    enabled: !!designator,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
  });
};

